import { useMemo } from 'react'

import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import {
  NETWORK_CONFIG,
  RailgunWalletBalanceBucket,
  TransactionHistoryItemCategory
} from '@railgun-community/shared-models'
import { ModeEnum } from '@web/contexts/appModeContext'
import useRailgunContext from '@web/hooks/useRailgunContext'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import { useRailgunShieldTxLog } from '@web/modules/shield/hooks/useRailgunShieldTxLog'
import {
  computeSecondsRemaining,
  formatApproxMinutesRemainingLabel,
  getPrivateHistoryItemBalanceBucket,
  getWaitTimeForShieldPending,
  type PrivateHistoryItem
} from '@web/utils/railgunTx'
import { getTotalBalanceUSD, type RailgunTokenBalance } from '@web/utils/railgunTokens'

export type UseRailgunShieldPendingResult = {
  shieldPendingCount: number
  showBadge: boolean
  singleUsdTotal: number | null
  singleTimeLeftLabel: string | null
  badgeText: string | null
  processingMultiCount: number | null
}

function toBigintAmount(amount: unknown): bigint {
  if (typeof amount === 'bigint') return amount
  if (typeof amount === 'number' && Number.isFinite(amount)) return BigInt(Math.trunc(amount))
  try {
    return BigInt(String(amount))
  } catch {
    return 0n
  }
}

export function useRailgunShieldPending(mode: 'public' | 'private'): UseRailgunShieldPendingResult {
  const { privateHistory, currentRailgunWallet } = useRailgunContext()
  const { account, dashboardNetworkFilter } = useSelectedAccountControllerState()
  const { records: shieldTxLog } = useRailgunShieldTxLog({ accountAddr: account?.addr })

  return useMemo(() => {
    const empty: UseRailgunShieldPendingResult = {
      shieldPendingCount: 0,
      showBadge: false,
      singleUsdTotal: null,
      singleTimeLeftLabel: null,
      badgeText: null,
      processingMultiCount: null
    }

    if (mode !== ModeEnum.PRIVATE) return empty

    const pendingShields = (privateHistory as PrivateHistoryItem[]).filter((item) => {
      if (item.category !== TransactionHistoryItemCategory.ShieldERC20s) return false
      return getPrivateHistoryItemBalanceBucket(item) === RailgunWalletBalanceBucket.ShieldPending
    })

    let scoped = pendingShields
    if (dashboardNetworkFilter != null) {
      if (dashboardNetworkFilter === 'rewards' || dashboardNetworkFilter === 'gasTank') {
        return empty
      }
      const filterStr = String(dashboardNetworkFilter)
      scoped = pendingShields.filter((item) => {
        const chainIdRaw = NETWORK_CONFIG[item.networkName]?.chain.id
        if (chainIdRaw == null) return false
        return String(chainIdRaw) === filterStr
      })
    }

    const count = scoped.length
    if (count === 0) return empty

    const prices = currentRailgunWallet?.balanceTokenPriceMap

    if (count >= 2) {
      return {
        shieldPendingCount: count,
        showBadge: true,
        singleUsdTotal: null,
        singleTimeLeftLabel: null,
        badgeText: null,
        processingMultiCount: count
      }
    }

    const item = scoped[0]!
    const chainIdRaw = NETWORK_CONFIG[item.networkName]?.chain.id
    const chainId = chainIdRaw != null ? Number(chainIdRaw) : undefined
    let singleUsdTotal: number | null = null
    if (chainId != null && !Number.isNaN(chainId) && item.receiveERC20Amounts?.length) {
      const balances: RailgunTokenBalance[] = item.receiveERC20Amounts.map((a) => ({
        tokenAddress: a.tokenAddress,
        amount: toBigintAmount(a.amount)
      }))
      singleUsdTotal =
        prices && prices.size > 0 ? getTotalBalanceUSD(new Map([[chainId, balances]]), prices) : 0
    }

    const record = shieldTxLog.find((r) => r.txHash === item.txid)
    const waitSec = getWaitTimeForShieldPending(item.networkName)
    const rawStart = item.timestamp ?? record?.timestamp
    const singleTimeLeftLabel =
      waitSec != null && rawStart !== undefined
        ? formatApproxMinutesRemainingLabel(computeSecondsRemaining(waitSec, rawStart))
        : null

    return {
      shieldPendingCount: 1,
      showBadge: true,
      singleUsdTotal,
      singleTimeLeftLabel,
      badgeText: `Processing ${[formatDecimals(singleUsdTotal ?? 0, 'value'), singleTimeLeftLabel]
        .filter(Boolean)
        .join(' ')}`,
      processingMultiCount: null
    }
  }, [
    mode,
    privateHistory,
    currentRailgunWallet?.balanceTokenPriceMap,
    shieldTxLog,
    dashboardNetworkFilter
  ])
}
